/* ═══════════════════════════════════════════════════════
   SwipeHome — Notifications Module
   Notification rendering, preferences, priority system
   ═══════════════════════════════════════════════════════ */

import { api } from './api.js';
import { goBack, renderTabBar } from './router.js';
import { state } from './state.js';
import type { Notification, NotificationPreferences } from './types/api.js';
import { closeModal, escapeHtml, showToast, timeAgo } from './ui.js';

// ─── Notification Priority System ──────────────────────
type NotifPriority = 'critical' | 'high' | 'medium' | 'low';

export const NOTIF_PRIORITY_MAP: Record<string, NotifPriority> = {
  offer_intent: 'critical',
  offer_received: 'critical',
  direct_message: 'critical',
  threshold_alert: 'high',
  agent_connected: 'high',
  new_like: 'medium',
  recommendation: 'medium',
  property_viewed: 'low',
  market_stats: 'low',
};

const NOTIF_PRIORITY_ORDER: Record<NotifPriority, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

export function getNotifPriority(type: string | undefined): NotifPriority {
  if (!type) return 'medium';
  return NOTIF_PRIORITY_MAP[type] || 'medium';
}

export function getNotifIcon(type: string | undefined): string {
  const map: Record<string, string> = {
                offer_intent: 'zap',
    new_like: 'heart',
    threshold_alert: 'trending-up',
    recommendation: 'star',
    agent_connected: 'users',
    offer_received: 'dollar-sign',
    direct_message: 'message-circle',
  };
  return (type && map[type]) || 'bell';
}

export function getNotifIconType(type: string | undefined): 'like' | 'alert' | 'system' {
  if (type?.includes('like') || type?.includes('threshold')) return 'like';
  if (type?.includes('alert') || type?.includes('warning')) return 'alert';
  return 'system';
}

// The batching step enriches notifications with transient UI-only
// fields that are NOT part of the wire shape.
interface EnrichedNotification extends Notification {
  message?: string;
  listing_address?: string | null;
  address_line1?: string | null;
  listing_price?: number | null;
  _priority?: NotifPriority;
  _batched?: boolean;
  _count?: number;
  _items?: Notification[];
}

interface NotifBatch {
  sender: string;
  type: string;
  items: EnrichedNotification[];
  latest: string;
  priority: NotifPriority;
}

export function batchNotifications(notifications: Notification[]): EnrichedNotification[] {
  const batches: Record<string, NotifBatch> = {};
  const standalone: EnrichedNotification[] = [];

  for (const raw of notifications) {
    const n = raw as EnrichedNotification;
    const priority = getNotifPriority(n.type);
    if (priority === 'medium' && n.type === 'new_like') {
      const msg = n.message || n.title || '';
      const senderMatch = msg.match(/^(.+?)\s+(liked|saved)/i);
      const sender = senderMatch ? senderMatch[1] : 'Someone';
      const hourBucket = n.created_at ? new Date(n.created_at).toISOString().slice(0, 13) : '';
      const key = `${sender}|${n.type}|${hourBucket}`;
      if (!batches[key]) {
        batches[key] = {
          sender,
          type: n.type,
          items: [],
          latest: n.created_at || '',
          priority,
        };
      }
      batches[key].items.push(n);
      if (n.created_at && new Date(n.created_at) > new Date(batches[key].latest)) {
        batches[key].latest = n.created_at;
      }
    } else {
      standalone.push({ ...n, _priority: priority, _batched: false });
    }
  }

  const result: EnrichedNotification[] = [...standalone];
  for (const [, batch] of Object.entries(batches)) {
    if (batch.items.length > 1) {
      const first = batch.items[0];
      result.push({
        ...first,
        title: `${batch.sender} liked ${batch.items.length} homes`,
        message: `${batch.sender} liked ${batch.items.length} homes`,
        created_at: batch.latest,
        read_at: batch.items.every((i) => i.read_at) ? first.read_at : null,
        _priority: batch.priority,
        _batched: true,
        _count: batch.items.length,
        _items: batch.items,
      });
    } else {
      result.push({ ...batch.items[0], _priority: batch.priority, _batched: false });
    }
  }

  result.sort((a, b) => {
    const pa = NOTIF_PRIORITY_ORDER[a._priority || 'medium'];
    const pb = NOTIF_PRIORITY_ORDER[b._priority || 'medium'];
    if (pa !== pb) return pa - pb;
    return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
  });

  return result;
}

let _notifFilter: 'all' | 'critical' | 'unread' = 'all';

export function setNotifFilter(f: 'all' | 'critical' | 'unread'): void {
  _notifFilter = f;
  document.querySelectorAll<HTMLElement>('.notif-filter-chip').forEach((c) => {
    c.classList.toggle('active', c.dataset.filter === f);
  });
  renderNotifList();
}

export function renderNotifications(): HTMLElement {
  void goBack; // exposed globally via window; quiet noUnusedLocals
  const div = document.createElement('div');
  div.className = 'fade-in';
  const isAgent = state.role === 'buyer_agent' || state.role === 'listing_agent';
  div.innerHTML = `
    <div class="screen-header">
      <button class="back-btn" onclick="goBack()"><i data-feather="arrow-left" width="18"></i></button>
      <h1>${isAgent ? 'Alerts' : 'Notifications'}</h1>
      <div class="header-actions">
        <button onclick="markAllRead()" title="Mark all read"><i data-feather="check-circle" width="18"></i></button>
        <button onclick="clearNotificationsPrompt()" title="Clear notifications"><i data-feather="trash-2" width="18"></i></button>
      </div>
    </div>
    <div class="screen" id="notifications-list">
      <div class="loading"><div class="spinner"></div></div>
    </div>
  `;

  setTimeout(loadNotifications, 50);
  return div;
}

interface NotificationsPayload {
  notifications?: Notification[];
  unread_count?: number;
}

export async function loadNotifications(): Promise<void> {
  const container = document.getElementById('notifications-list');
  if (!container) return;

  try {
    const data = await api<NotificationsPayload>('GET', '/notifications?limit=50');
    state.notifications = data.notifications || [];
    state.unreadCount = state.notifications.filter((n) => !n.read_at).length;

    const tabBar = document.getElementById('tab-bar');
    if (tabBar) renderTabBar(tabBar);
    renderNotifList();
    if (window.feather) window.feather.replace();
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to load notifications';
    container.innerHTML = `<div class="empty-state"><p class="text-danger">${escapeHtml(message)}</p></div>`;
  }
}

function renderNotifList(): void {
  const container = document.getElementById('notifications-list');
  if (!container) return;

  if (state.notifications.length === 0) {
    container.innerHTML = `
      <div class="empty-state">
        <i data-feather="bell-off" width="48"></i>
        <h3>No notifications</h3>
        <p>You're all caught up!</p>
      </div>
    `;
    if (window.feather) window.feather.replace();
    return;
  }

  const batched = batchNotifications(state.notifications);
  const isAgent = state.role === 'buyer_agent' || state.role === 'listing_agent';

  let filtered = batched;
  if (_notifFilter === 'critical') {
    filtered = batched.filter((n) => (n._priority || getNotifPriority(n.type)) === 'critical');
  } else if (_notifFilter === 'unread') {
    filtered = batched.filter((n) => !n.read_at);
  }

  const critical = filtered.filter((n) => (n._priority || getNotifPriority(n.type)) === 'critical');
  const high = filtered.filter((n) => (n._priority || getNotifPriority(n.type)) === 'high');
  const rest = filtered.filter((n) => {
    const p = n._priority || getNotifPriority(n.type);
    return p === 'medium' || p === 'low';
  });

  const critCount = batched.filter(
    (n) => (n._priority || getNotifPriority(n.type)) === 'critical' && !n.read_at,
  ).length;

  const priorityStyles: Record<NotifPriority, string> = {
    critical: 'notif-priority-critical',
    high: 'notif-priority-high',
    medium: 'notif-priority-medium',
    low: 'notif-priority-low',
  };
  const priorityDots: Record<NotifPriority, string> = {
    critical: '\u{1F534}',
    high: '\u{1F7E0}',
    medium: '\u{1F7E1}',
    low: '\u{1F7E2}',
  };

  function renderNotifItem(n: EnrichedNotification): string {
    const priority = n._priority || getNotifPriority(n.type);
    const iconType = getNotifIconType(n.type);
    const addr = n.listing_address || n.address_line1 || '';
    const listingPrice = n.listing_price ? `$${Number(n.listing_price).toLocaleString()}` : '';
    const hasContext = addr || listingPrice;
    const batchLabel = n._batched ? `<span class="notif-batch-count">${n._count}</span>` : '';

    return `
      <div class="notification-item ${n.read_at ? '' : 'unread'} ${priorityStyles[priority]}" id="notif-${n.id}">
        <div style="display:flex;align-items:flex-start;gap:12px;flex:1;min-width:0" onclick="markNotifRead('${n.id}')">
          <div class="notification-icon ${iconType}">
            <i data-feather="${getNotifIcon(n.type)}" width="18"></i>
          </div>
          <div class="notification-content">
            <div class="notification-text">${batchLabel}${escapeHtml(n.message || n.title || '')}</div>
            ${hasContext ? `
              <div class="notification-listing-context">
                <i data-feather="map-pin" width="12"></i>
                <span>${escapeHtml(String(addr))}${addr && listingPrice ? ' \u00b7 ' : ''}${listingPrice}</span>
              </div>
            ` : ''}
            <div class="notification-time">${timeAgo(n.created_at)}</div>
          </div>
        </div>
        ${!n.read_at ? '<div class="notification-dot"></div>' : ''}
        <button class="notif-dismiss-btn" onclick="event.stopPropagation(); dismissNotification('${n.id}')" title="Dismiss">
          <i data-feather="x" width="14"></i>
        </button>
      </div>
    `;
  }

  container.innerHTML = `
    ${isAgent ? `
      <div class="notif-filters" style="padding:0 16px 12px">
        <button class="notif-filter-chip ${_notifFilter === 'all' ? 'active' : ''}" data-filter="all" onclick="setNotifFilter('all')">All</button>
        <button class="notif-filter-chip ${_notifFilter === 'critical' ? 'active' : ''}" data-filter="critical" onclick="setNotifFilter('critical')">
          Action Required ${critCount > 0 ? `<span class="notif-filter-badge">${critCount}</span>` : ''}
        </button>
        <button class="notif-filter-chip ${_notifFilter === 'unread' ? 'active' : ''}" data-filter="unread" onclick="setNotifFilter('unread')">Unread</button>
      </div>
    ` : ''}

    <div class="notification-list">
      ${critical.length > 0 ? `
        <div class="notif-section-header notif-section-critical">
          <span>${priorityDots.critical} Requires Action</span>
        </div>
        ${critical.map(renderNotifItem).join('')}
      ` : ''}

      ${high.length > 0 ? `
        <div class="notif-section-header notif-section-high">
          <span>${priorityDots.high} Important</span>
        </div>
        ${high.map(renderNotifItem).join('')}
      ` : ''}

      ${rest.length > 0 ? `
        <div class="notif-section-header notif-section-recent">
          <span>${priorityDots.medium} Recent Activity</span>
        </div>
        ${rest.map(renderNotifItem).join('')}
      ` : ''}

      ${filtered.length === 0 ? `
        <div class="empty-state" style="padding:40px 0">
          <i data-feather="check" width="36"></i>
          <p style="margin-top:8px;color:var(--text-secondary)">No ${_notifFilter === 'critical' ? 'critical alerts' : 'unread notifications'}</p>
        </div>
      ` : ''}
    </div>
  `;
  if (window.feather) window.feather.replace();
}

export async function markNotifRead(id: string): Promise<void> {
  try {
    await api('PATCH', `/notifications/${id}/read`);
    const notif = state.notifications.find((n) => n.id === id);
    if (notif) notif.read_at = new Date().toISOString();
    state.unreadCount = state.notifications.filter((n) => !n.read_at).length;
    const tabBar = document.getElementById('tab-bar');
    if (tabBar) renderTabBar(tabBar);
    if (window.feather) window.feather.replace();
    loadNotifications();
  } catch {
    /* ignore */
  }
}

export async function markAllRead(): Promise<void> {
  try {
    await api('POST', '/notifications/read-all');
    state.unreadCount = 0;
    showToast('All notifications marked as read', 'success');
    loadNotifications();
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to mark all read';
    showToast(message, 'error');
  }
}

export async function dismissNotification(id: string): Promise<void> {
  const el = document.getElementById(`notif-${id}`);
  if (el) {
    el.style.transition = 'transform 0.25s ease, opacity 0.25s ease';
    el.style.transform = 'translateX(100%)';
    el.style.opacity = '0';
  }
  try {
    await api('DELETE', `/notifications/${id}`);
    state.notifications = state.notifications.filter((n) => n.id !== id);
    state.unreadCount = state.notifications.filter((n) => !n.read_at).length;
    const tabBar = document.getElementById('tab-bar');
    if (tabBar) renderTabBar(tabBar);
    setTimeout(() => renderNotifList(), 250);
  } catch (err) {
    if (el) {
      el.style.transform = '';
      el.style.opacity = '';
    }
    const message = err instanceof Error ? err.message : 'Failed to dismiss';
    showToast(message, 'error');
  }
}

export async function clearNotificationsPrompt(): Promise<void> {
  const total = state.notifications.length;
  if (total === 0) {
    showToast('No notifications to clear', 'info');
    return;
  }

  const modal = document.getElementById('modal-container');
  if (!modal) return;
  modal.classList.remove('hidden');
  modal.innerHTML = `
    <div class="modal-overlay" onclick="closeModal()"></div>
    <div class="modal-sheet" style="max-height:60vh">
      <div class="modal-handle"></div>
      <div class="modal-header">
        <h2>Clear Notifications</h2>
        <button class="modal-close" onclick="closeModal()"><i data-feather="x" width="20"></i></button>
      </div>
      <div class="modal-body" style="padding:16px">
        <button class="btn btn-block" style="background:var(--surface-alt);border:1px solid var(--border);color:var(--text);padding:14px;border-radius:12px;font-size:14px;font-weight:600;margin-bottom:10px;text-align:left" onclick="clearNotifications('read')">
          <div>Clear read notifications</div>
          <div style="font-size:12px;font-weight:400;color:var(--text-secondary);margin-top:2px">Remove notifications you've already seen</div>
        </button>
        <button class="btn btn-block" style="background:var(--danger-bg);border:1px solid var(--danger-border);color:var(--danger);padding:14px;border-radius:12px;font-size:14px;font-weight:600;text-align:left" onclick="clearNotifications('all')">
          <div>Clear all notifications</div>
          <div style="font-size:12px;font-weight:400;color:var(--danger);opacity:0.7;margin-top:2px">Remove all ${total} notification${total !== 1 ? 's' : ''}</div>
        </button>
      </div>
    </div>
  `;
  if (window.feather) window.feather.replace();
}

export async function clearNotifications(scope: 'read' | 'all'): Promise<void> {
  closeModal();
  try {
    const result = await api<{ deleted?: number }>('DELETE', `/notifications?scope=${scope}`);
    const deleted = result.deleted || 0;
    showToast(`Cleared ${deleted} notification${deleted !== 1 ? 's' : ''}`, 'success');
    await loadNotifications();
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to clear';
    showToast(message, 'error');
  }
}

// ═══════════════════════════════════════════════════════
// Notification Preferences Screen
// ═══════════════════════════════════════════════════════

interface NotificationChannelPrefs {
  in_app: boolean;
  push: boolean;
  email: boolean;
}

// The backend returns a strongly-typed NotificationPreferences object,
// but the frontend panel stores a looser shape with per-channel toggles.
// The _notifPrefs stash on state accommodates both.
interface ExtendedNotifPrefs {
  categories?: Record<string, NotificationChannelPrefs>;
  quiet_hours_start?: string | null;
  quiet_hours_end?: string | null;
  batch_digest?: boolean;
}

function getNotifPrefsStash(): { _notifPrefs: ExtendedNotifPrefs | null } {
  return state as unknown as { _notifPrefs: ExtendedNotifPrefs | null };
}

export function renderNotificationPrefs(): HTMLElement {
  const div = document.createElement('div');
  div.className = 'fade-in';

  div.innerHTML = `
    <div class="screen-header">
      <button class="back-btn" onclick="goBack()"><i data-feather="arrow-left" width="18"></i></button>
      <h1>Notification Preferences</h1>
      <div style="width:44px"></div>
    </div>
    <div class="screen-padded" id="notif-prefs-content">
      <div class="loading"><div class="spinner"></div></div>
    </div>
  `;

  setTimeout(loadNotificationPrefs, 50);
  return div;
}

export async function loadNotificationPrefs(): Promise<void> {
  const container = document.getElementById('notif-prefs-content');
  if (!container) return;

  try {
    const data = await api<NotificationPreferences & ExtendedNotifPrefs>(
      'GET',
      '/users/notification-preferences',
    );
    getNotifPrefsStash()._notifPrefs = data;

    const categories: Record<string, NotificationChannelPrefs> = data.categories || {};
    const labels: Record<string, { name: string; desc: string; icon: string }> = {
      swipe_activity: {
        name: 'Swipe Activity',
        desc: 'When your clients like or pass on properties',
        icon: 'heart',
      },
      price_changes: { name: 'Price Changes', desc: 'Price updates on liked properties', icon: 'dollar-sign' },
      recommendations: { name: 'Recommendations', desc: 'Agent property recommendations', icon: 'star' },
      messages: { name: 'Messages', desc: 'New messages and replies', icon: 'message-square' },
      threshold_alerts: {
        name: 'Listing Milestones',
        desc: 'When listings hit like milestones',
        icon: 'trending-up',
      },
      market_updates: { name: 'Market Updates', desc: 'Market trends and insights', icon: 'bar-chart-2' },
    };

    let html = '';
    for (const [key, meta] of Object.entries(labels)) {
      const cat: NotificationChannelPrefs = categories[key] || { in_app: true, push: true, email: false };
      html += `
        <div style="padding:16px 0;border-bottom:1px solid var(--border)">
          <div style="display:flex;align-items:center;gap:10px;margin-bottom:8px">
            <i data-feather="${meta.icon}" width="18" style="color:var(--primary)"></i>
            <div>
              <div style="font-size:14px;font-weight:600;color:var(--text)">${meta.name}</div>
              <div style="font-size:12px;color:var(--text-secondary)">${meta.desc}</div>
            </div>
          </div>
          <div style="display:flex;gap:12px;margin-left:28px">
            <label style="display:flex;align-items:center;gap:6px;font-size:13px;color:var(--text-secondary);cursor:pointer">
              <input type="checkbox" ${cat.in_app ? 'checked' : ''} onchange="toggleNotifPref('${key}', 'in_app', this.checked)" style="accent-color:var(--primary)">
              In-app
            </label>
            <label style="display:flex;align-items:center;gap:6px;font-size:13px;color:var(--text-secondary);cursor:pointer">
              <input type="checkbox" ${cat.push ? 'checked' : ''} onchange="toggleNotifPref('${key}', 'push', this.checked)" style="accent-color:var(--primary)">
              Push
            </label>
            <label style="display:flex;align-items:center;gap:6px;font-size:13px;color:var(--text-secondary);cursor:pointer">
              <input type="checkbox" ${cat.email ? 'checked' : ''} onchange="toggleNotifPref('${key}', 'email', this.checked)" style="accent-color:var(--primary)">
              Email
            </label>
          </div>
        </div>
      `;
    }

    html += `
      <div style="padding:16px 0;border-bottom:1px solid var(--border)">
        <div style="display:flex;align-items:center;gap:10px;margin-bottom:12px">
          <i data-feather="moon" width="18" style="color:var(--primary)"></i>
          <div>
            <div style="font-size:14px;font-weight:600;color:var(--text)">Quiet Hours</div>
            <div style="font-size:12px;color:var(--text-secondary)">Pause non-urgent notifications</div>
          </div>
        </div>
        <div style="display:flex;gap:12px;margin-left:28px;align-items:center">
          <input type="time" id="quiet-start" value="${data.quiet_hours_start || ''}" onchange="updateQuietHours()" style="background:var(--surface-alt);border:1px solid var(--border);border-radius:8px;padding:6px 10px;color:var(--text);font-size:13px">
          <span style="color:var(--text-muted)">to</span>
          <input type="time" id="quiet-end" value="${data.quiet_hours_end || ''}" onchange="updateQuietHours()" style="background:var(--surface-alt);border:1px solid var(--border);border-radius:8px;padding:6px 10px;color:var(--text);font-size:13px">
        </div>
      </div>
    `;

    html += `
      <div style="padding:16px 0">
        <label style="display:flex;align-items:center;gap:10px;cursor:pointer">
          <input type="checkbox" ${data.batch_digest ? 'checked' : ''} onchange="toggleBatchDigest(this.checked)" style="accent-color:var(--primary)">
          <div>
            <div style="font-size:14px;font-weight:600;color:var(--text)">Daily Digest</div>
            <div style="font-size:12px;color:var(--text-secondary)">Batch low-priority notifications into a daily summary</div>
          </div>
        </label>
      </div>
    `;

    container.innerHTML = html;
    if (window.feather) window.feather.replace();
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to load preferences';
    container.innerHTML = `<div class="empty-state"><p class="text-danger">${escapeHtml(message)}</p></div>`;
  }
}

export async function toggleNotifPref(
  category: string,
  channel: keyof NotificationChannelPrefs,
  enabled: boolean,
): Promise<void> {
  const stash = getNotifPrefsStash();
  if (!stash._notifPrefs) return;
  const cats = stash._notifPrefs.categories || {};
  const cat: NotificationChannelPrefs = cats[category] || { in_app: true, push: true, email: false };
  cat[channel] = enabled;
  cats[category] = cat;
  stash._notifPrefs.categories = cats;

  try {
    await api('PUT', '/users/notification-preferences', stash._notifPrefs);
  } catch {
    showToast('Failed to save preference', 'error');
  }
}

export async function updateQuietHours(): Promise<void> {
  const start = (document.getElementById('quiet-start') as HTMLInputElement | null)?.value || null;
  const end = (document.getElementById('quiet-end') as HTMLInputElement | null)?.value || null;
  const stash = getNotifPrefsStash();
  if (!stash._notifPrefs) stash._notifPrefs = {};
  stash._notifPrefs.quiet_hours_start = start;
  stash._notifPrefs.quiet_hours_end = end;

  try {
    await api('PUT', '/users/notification-preferences', stash._notifPrefs);
  } catch {
    showToast('Failed to save quiet hours', 'error');
  }
}

export async function toggleBatchDigest(enabled: boolean): Promise<void> {
  const stash = getNotifPrefsStash();
  if (!stash._notifPrefs) stash._notifPrefs = {};
  stash._notifPrefs.batch_digest = enabled;

  try {
    await api('PUT', '/users/notification-preferences', stash._notifPrefs);
  } catch {
    showToast('Failed to save preference', 'error');
  }
}
